/**
 * ComprasApiClient -- HTTP client real para facturas de compras + movimientos de inventario.
 *
 * Migrado mock->real en T1P / PANEL-COMPRAS-REAL-API-MIGRATION-EDIT-V1-001 (bit #995).
 * Edicion agregada en T2P+T3P+T4P (bit #1002, NexoAPI v1.6.19+).
 * DELETE factura agregado en F1-P / PANEL-COMPRAS-EDICION-AVANZADA-V1-001 (bit #1038).
 * Endpoints:
 *   GET    /api/v1/businesses/{businessId}/compras                                       (listFacturas)
 *   GET    /api/v1/businesses/{businessId}/compras/{facturaId}                           (getFacturaById)
 *   PATCH  /api/v1/businesses/{businessId}/compras/{facturaId}                           (updateFactura T2P)
 *   DELETE /api/v1/businesses/{businessId}/compras/{facturaId}?force=true|false          (deleteFactura F1-P)
 *   PATCH  /api/v1/businesses/{businessId}/compras/{facturaId}/items/{itemId}            (updateItem T3P)
 *   POST   /api/v1/businesses/{businessId}/compras/{facturaId}/verificacion              (verificarFactura FV-T4)
 *   GET    /api/v1/businesses/{businessId}/movimientos-inventario/estadisticas           (getEstadisticasMovimientos MV-T5)
 *   GET    /api/v1/businesses/{businessId}/movimientos-inventario                        (listMovimientos)
 *   POST   /api/v1/businesses/{businessId}/movimientos-inventario                        (createMovimiento)
 *   PATCH  /api/v1/businesses/{businessId}/movimientos-inventario/{movId}                (updateMovimiento T4P)
 *   DELETE /api/v1/businesses/{businessId}/movimientos-inventario/{movId}                (deleteMovimiento T4P)
 *
 * Adapter shapes (panel<->API): la respuesta backend usa camelCase + matchCounts agregados,
 * el panel UI consume snake_case + match_status string. Mappers inline mantienen interface
 * publica estable para no tocar ComprasView.js downstream.
 *
 * @module infrastructure/api/ComprasApiClient
 */

import { ApiBase } from './ApiBase.js';
import { AppConfig } from '../../config.js';

const BASE = '/api/v1/businesses';

/**
 * Desempaqueta la response del workflow Purchase V1.0 (OCR Gemini).
 * El nodo Gemini retorna { content: { parts: [{ text: "<JSON-string>" }] } };
 * el JSON real (resumen/tabla/factura_id) viene como STRING dentro de text.
 * 3 paths en orden de probabilidad:
 *   1. Gemini wrapper (productivo): parsed.content.parts[0].text -> JSON.parse
 *   2. parsed.output.* (legacy/alternative)
 *   3. parsed.* (root, si el workflow ya hizo el unwrap)
 */
function unwrapGeminiPayload(parsed) {
  let payload = null;
  const geminiText = parsed?.content?.parts?.[0]?.text;
  if (typeof geminiText === 'string') {
    try {
      payload = JSON.parse(geminiText.replace(/```json\s*/i, '').replace(/```/g, '').trim());
    } catch { payload = null; }
  }
  if (!payload) payload = parsed?.output || parsed || {};
  return payload;
}

// ============== Adapters shape API real -> shape panel ==============

/**
 * Deriva match_status de factura (panel: PENDING|MATCH|REVIEW) a partir de
 * matchCounts agregados por items (real: total, pending, match, create, review).
 *
 * Reglas:
 *   - total === 0                                       -> 'PENDING' (factura sin items aun)
 *   - total === match (todo resuelto OK)                -> 'MATCH'
 *   - review > 0 || create > 0 || (pending && match)    -> 'REVIEW' (mezcla / requiere humano)
 *   - resto (solo pending, match=0)                     -> 'PENDING'
 */
function deriveMatchStatus(mc) {
  if (!mc || typeof mc !== 'object') return 'PENDING';
  const total = mc.total || 0;
  const pending = mc.pending || 0;
  const match = mc.match || 0;
  const create = mc.create || 0;
  const review = mc.review || 0;
  if (total === 0) return 'PENDING';
  if (match === total) return 'MATCH';
  if (review > 0 || create > 0 || (pending > 0 && match > 0)) return 'REVIEW';
  return 'PENDING';
}

/** Mapea ComprasFacturaListItemDto -> shape Factura panel. */
function adaptFactura(api) {
  if (!api) return null;
  // B8-P fix: fallback robusto para proveedor (cubrir nulls/undefined desde backend).
  // Si todos los campos proveedor vienen vacios, dejar string vacio (formatNullable
  // downstream lo convierte a "—" defensivamente).
  const proveedorNombre = [api.proveedorMarca, api.proveedorRazonSocial]
    .map(v => (v == null ? '' : String(v).trim()))
    .find(v => v !== '' && v !== 'undefined' && v !== 'null');
  return {
    id: api.facturaId,
    fecha: api.fechaFactura || '',
    hora: api.horaFactura || '',
    proveedor: proveedorNombre || '',
    proveedor_id: api.proveedorId,
    // FV-T5 punto 1: el rubro que el proveedor YA tiene (proveedores.rubro_id), pedido a A el 13-oct. null hasta que
    // la API lo mande: la revision lo pregunta como hoy.
    proveedor_rubro_id: api.proveedorRubroId ?? null,
    proveedor_rubro_nombre: api.proveedorRubroNombre ?? null,
    proveedor_nit: api.proveedorNit || '',
    numero: api.codigo || '',
    total: api.total || 0,
    valor_base: api.valorBase || 0,
    iva: api.iva || 0,
    moneda: api.moneda || 'COP',
    created_at: api.createdAt || null, // B8-P: para mostrar hora_registro local Colombia
    // FV-T5 (bit #3355): gate de verificacion por colaborador. NULL-SAFE: si el
    // backend (FV-T1) aun no expone la columna, queda null y la UI no pinta badge
    // -- el comportamiento actual (solo doble gate por match) no cambia.
    verificacion_status: api.verificacionStatus ?? null,
    match_status: deriveMatchStatus(api.matchCounts),
    items_count: api.matchCounts?.total || 0,
    match_counts: api.matchCounts || { total: 0, pending: 0, match: 0, create: 0, review: 0 }
  };
}

/** Mapea ComprasFacturaItemDto -> shape item panel. */
function adaptFacturaItem(api) {
  if (!api) return null;
  const candidatos = Array.isArray(api.candidatos) ? api.candidatos.map(c => ({
    producto_existente_id: c.insumoId,
    nombre: c.nombre || c.nombreNormalizado || '',
    presentacion: c.presentacion || '',
    unidad: c.unidad || '',
    costo_ref: c.costoRef || 0,
    similitud: c.score || 0,
    // AL-T1 (nexo-api 654): el candidato es un insumo que se procesa al llegar.
    requiere_procesamiento: c.requiereProcesamiento === true
  })) : [];
  return {
    id: api.id,
    producto: api.insumoNombre || api.descripcionRaw || '',
    descripcion_raw: api.descripcionRaw || '',
    insumo_id: api.insumoId || null,
    insumo_nombre: api.insumoNombre || '',
    // AL-T1 (nexo-api 654): el insumo casado se procesa al llegar; el detalle lo avisa (sin tabla de alertas).
    insumo_requiere_procesamiento: api.insumoRequiereProcesamiento === true,
    presentacion: api.presentacion || '',
    unidad: api.unidad || '',
    cantidad: api.cantidad || 0,
    precio_unit: api.precioUnitario || 0,
    subtotal: api.totalLinea || 0,
    iva_pct: api.ivaPct || 0,
    match_status: api.matchStatus || 'PENDING',
    match_score: api.matchScore || 0,
    confianza_item: api.confianzaItem || 0,
    alertas_item: Array.isArray(api.alertasItem) ? api.alertasItem : [],
    // FV-T5 (bit #3355, FACTURA-VERIFY-COLAB-V1-001): datos de empaque del OCR.
    // NULL-SAFE A PROPOSITO: mientras FV-T1/FV-T2 no esten desplegados el backend
    // no manda estos campos y quedan en null -- la UI los omite y se ve igual que
    // hoy. Caso real Badur 1318625: OCR leyo 5 und de Coca Cola 2.5 cuando la caja
    // trae 8 (factor X8); Gemini SI calcula el factor pero n8n lo descartaba.
    //   cantidad_factura = lo que dice el papel (5 cajas)
    //   factor_empaque   = unidades por empaque (8)
    //   cantidad         = unidades reales al inventario (40) = cantidad_factura * factor
    cantidad_factura: api.cantidadFactura ?? null,
    unidad_factura: api.unidadFactura ?? null,
    factor_empaque: api.factorEmpaque ?? null,
    precio_unitario_factura: api.precioUnitarioFactura ?? null,
    // COMPRAS-BORRADOR-ANULACION-T1 (contrato v0.2 #31032, API 1.6.811): es_inventariable es la SUPOSICION del OCR
    // (solo precarga la casilla); sin_inventario_por es la marca de una persona y cuenta como resuelto.
    es_inventariable: api.esInventariable ?? api.EsInventariable ?? null,
    sin_inventario_por: api.sinInventarioPor ?? api.SinInventarioPor ?? null,
    sin_inventario_at: api.sinInventarioAt ?? api.SinInventarioAt ?? null,
    candidatos
  };
}

/** Mapea MovimientoInventarioResponseDto -> shape movimiento panel. */
function adaptMovimiento(api) {
  if (!api) return null;
  return {
    id: api.id,
    tipo: (api.tipo || '').toLowerCase(),
    referencia: api.referencia || '',
    fecha: api.fechaOperativa || '',
    hora: api.horaOperativa || '',
    insumo_id: api.insumoId,
    insumo_nombre: api.insumoNombre || '',
    insumo_presentacion: api.insumoPresentacion || '',
    cantidad: api.cantidad || 0,
    factura_item_id: api.facturaItemId || null,
    items_count: 1,
    total: api.cantidad || 0,
    usuario: api.usuario || '',
    motivo: api.referencia || '',
    created_at: api.createdAt || ''
  };
}

/**
 * Mapea la respuesta de detalle de factura (header + items + movimientos) al shape
 * plano que consume el panel. Extraido en FV-T4 (bit #3363) porque el POST de
 * verificacion devuelve EXACTAMENTE el mismo shape que el GET detalle (contrato
 * #3362) -- un solo adapter para las dos entradas, cero divergencia.
 */
function adaptFacturaDetalle(apiData) {
  const header = adaptFactura(apiData?.header) || {};
  const items = Array.isArray(apiData?.items) ? apiData.items.map(adaptFacturaItem).filter(Boolean) : [];
  const movimientos_generados = Array.isArray(apiData?.movimientosGenerados)
    ? apiData.movimientosGenerados.map(adaptMovimiento).filter(Boolean)
    : [];
  return { ...header, items, movimientos_generados };
}

/** Mapea filtro panel match -> filtro API real estado. */
function mapEstadoFilter(matchFilter) {
  if (!matchFilter) return 'todos';
  if (matchFilter === 'MATCH') return 'resueltas';
  if (matchFilter === 'PENDING' || matchFilter === 'REVIEW') return 'pendientes';
  return 'todos';
}

/**
 * Construye body sparse para PATCH a partir de un dict de fields panel->api.
 * Solo incluye keys cuyo valor en `panelObj` es !== undefined. Esto permite
 * "no actualizar este campo" (constraint Ares: NO null si no se quiere borrar).
 */
function buildSparseBody(panelObj, mapping) {
  const body = {};
  for (const [panelKey, apiKey] of Object.entries(mapping)) {
    if (panelObj[panelKey] !== undefined) {
      body[apiKey] = panelObj[panelKey];
    }
  }
  return body;
}

// ============== API publica ==============

export const ComprasApiClient = {
  /**
   * Sube UNA imagen/PDF de factura al workflow Purchase V1.0 (n8n OCR Gemini) y
   * devuelve el payload YA desempaquetado + un resumen normalizado.
   *
   * URL unica reutilizable (KIOSK-ARGEMIRO-COMPRA-V1): antes el webhook estaba
   * hardcodeado en ComprasView.js:930. Ahora lo consumen:
   *   - ComprasView Tab4 (flujo manager): usa `.payload` para su match-review.
   *   - ArgemiroKioskView (kiosk colaborador): usa el resumen (proveedor/total/
   *     itemsCount) para renderizar la respuesta como mensaje de Argemiro.
   *
   * NO modifica el workflow n8n (contrato SOLO lectura). Los campos operador_*
   * son extra en el FormData; el workflow los ignora si no los mapea (T5 traza).
   *
   * @param {string} businessId
   * @param {File} file  imagen o PDF de la factura
   * @param {{ operadorId?:string, operadorNombre?:string, operadorPin?:string, source?:string }} meta
   * @returns {Promise<{ ok:boolean, status:number, payload:object|null, resumen:object, tabla:Array, facturaId:string|null, proveedor:string, total:number|null, itemsCount:number, pregunta:string|null, error?:string }>}
   */
  async uploadOcr(businessId, file, meta = {}) {
    const empty = { ok: false, status: 0, payload: null, resumen: {}, tabla: [], facturaId: null, proveedor: '', total: null, itemsCount: 0, pregunta: null };
    if (!businessId) return { ...empty, error: 'BUSINESS_REQUIRED' };
    if (!file)       return { ...empty, error: 'FILE_REQUIRED' };

    const url = `${AppConfig.n8nBase}/webhook/purchase`;
    const fd = new FormData();
    fd.append('data', file, file.name);
    fd.append('business_id', businessId);
    // T5 trazabilidad: quien captura desde el kiosk. Campos extra ignorados por
    // el workflow si no los usa (no rompe el flujo manager que no los envia).
    if (meta.operadorId)     fd.append('operador_id', meta.operadorId);
    if (meta.operadorNombre) fd.append('operador_nombre', meta.operadorNombre);
    if (meta.operadorPin)    fd.append('operador_pin', meta.operadorPin);
    fd.append('source', meta.source || 'panel');

    try {
      const res = await fetch(url, { method: 'POST', body: fd });
      if (!res.ok) {
        let errText = '';
        try { errText = await res.text(); } catch { errText = ''; }
        return { ...empty, status: res.status, error: errText.substring(0, 500) };
      }
      let parsed = null;
      try { parsed = await res.json(); } catch { parsed = null; }

      const payload = unwrapGeminiPayload(parsed);
      const resumen = payload?.resumen || {};
      const tabla = Array.isArray(payload?.tabla) ? payload.tabla : [];
      const facturaInfo = payload?.factura || {};
      const proveedor = String(
        payload?.proveedor
        ?? facturaInfo.proveedor ?? facturaInfo.Proveedor
        ?? resumen.proveedor ?? ''
      ).trim();
      const totalRaw = payload?.factura_total
        ?? facturaInfo.Total ?? facturaInfo.total
        ?? resumen.total ?? null;
      const itemsCount = Number(resumen.items_total) || tabla.length || 0;

      return {
        ok: true,
        status: res.status,
        payload,
        resumen,
        tabla,
        facturaId: payload?.factura_id ?? null,
        proveedor,
        total: totalRaw != null && totalRaw !== '' ? Number(totalRaw) : null,
        itemsCount,
        pregunta: payload?.pregunta_al_usuario || null
      };
    } catch (err) {
      return { ...empty, status: 0, error: err?.message || 'NETWORK' };
    }
  },

  /**
   * Lista facturas de compras con filtros.
   * @param {string} businessId
   * @param {{ from?:string, to?:string, match?:'PENDING'|'MATCH'|'REVIEW'|'', proveedor?:string, page?:number, pageSize?:number }} filters
   */
  async listFacturas(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    const qs = new URLSearchParams();
    qs.set('estado', mapEstadoFilter(filters.match));
    if (filters.from) qs.set('desde', filters.from);
    if (filters.to)   qs.set('hasta', filters.to);
    if (filters.page)     qs.set('page',     String(filters.page));
    if (filters.pageSize) qs.set('pageSize', String(filters.pageSize));

    const res = await ApiBase.get(`${BASE}/${businessId}/compras?${qs.toString()}`);
    if (!res.ok) return res;

    // ApiBase.get devuelve { ok, data: ApiResponse<T> } -> data.data tiene el wrapper real
    const apiData = res.data?.data || res.data || {};
    const apiItems = Array.isArray(apiData.items) ? apiData.items : [];
    let items = apiItems.map(adaptFactura).filter(Boolean);

    // Filtro client-side de match espejo: el backend con estado='pendientes' devuelve
    // todo lo no-resuelto (PENDING + REVIEW + CREATE en items). Refinamos:
    if (filters.match === 'PENDING') {
      items = items.filter(f => f.match_status === 'PENDING');
    } else if (filters.match === 'REVIEW') {
      items = items.filter(f => f.match_status === 'REVIEW');
    }

    // Filtro client-side por proveedor (string libre, el API real usa proveedorId UUID).
    if (filters.proveedor) {
      const q = String(filters.proveedor).toLowerCase();
      items = items.filter(f => f.proveedor.toLowerCase().includes(q));
    }

    return {
      ok: true,
      data: {
        items,
        totalCount: typeof apiData.total === 'number' ? apiData.total : items.length,
        page: apiData.page || 1,
        pageSize: apiData.pageSize || items.length,
        totales: {
          totalFacturas: items.reduce((a, f) => a + (f.total || 0), 0),
          countByMatch: {
            PENDING: items.filter(f => f.match_status === 'PENDING').length,
            MATCH:   items.filter(f => f.match_status === 'MATCH').length,
            REVIEW:  items.filter(f => f.match_status === 'REVIEW').length
          }
        }
      }
    };
  },

  /**
   * Obtiene detalle completo de una factura (header + items + candidatos + movimientos generados).
   * @param {string} businessId
   * @param {string} facturaId  UUID de la factura
   */
  async getFacturaById(businessId, facturaId) {
    if (!businessId || !facturaId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const res = await ApiBase.get(`${BASE}/${businessId}/compras/${facturaId}`);
    if (!res.ok) return res;

    return { ok: true, data: adaptFacturaDetalle(res.data?.data || res.data || {}) };
  },

  /**
   * FV-T4 (bit #3363): aprueba (o solo corrige) la verificacion de una factura por
   * parte del colaborador. Contrato CONGELADO por Argy-A en bitacora #3362:
   *
   *   POST /businesses/{bid}/compras/{fid}/verificacion
   *   { aprobar: bool, items?: [{ id, cantidadFactura?, factorEmpaque?, cantidad? }] }
   *
   * - `items` omitido o [] es VALIDO y es el caso comun: el OCR acerto y el
   *   colaborador solo confirma.
   * - Se manda cantidadFactura + factorEmpaque y el BACKEND deriva la cantidad
   *   (cantidad = cantidadFactura * factorEmpaque). No mandamos las tres a la vez:
   *   el backend responde 400 fail-loud si no cuadran, a proposito.
   * - La respuesta es la factura RECALCULADA completa (mismo shape que el GET
   *   detalle), asi el kiosk pinta lo que calculo el servidor sin un segundo GET.
   *
   * @param {string} businessId
   * @param {string} facturaId
   * @param {{ aprobar:boolean, items?:Array<{id:string, cantidadFactura?:number, factorEmpaque?:number, cantidad?:number}> }} payload
   */
  async verificarFactura(businessId, facturaId, payload = {}) {
    if (!businessId || !facturaId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };
    if (typeof payload.aprobar !== 'boolean') {
      return { ok: false, error: { code: 'PAYLOAD_INVALID', message: 'aprobar (bool) es obligatorio' } };
    }

    const items = Array.isArray(payload.items) ? payload.items.filter(i => i && i.id).map(i => {
      const out = { id: i.id };
      if (i.cantidadFactura !== undefined && i.cantidadFactura !== null) out.cantidadFactura = Number(i.cantidadFactura);
      if (i.factorEmpaque !== undefined && i.factorEmpaque !== null) out.factorEmpaque = Number(i.factorEmpaque);
      // `cantidad` es el escape hatch del contrato: solo viaja si NO mandamos los
      // otros dos (si van los tres y no cuadran, el backend devuelve 400).
      if (out.cantidadFactura === undefined && out.factorEmpaque === undefined &&
          i.cantidad !== undefined && i.cantidad !== null) out.cantidad = Number(i.cantidad);
      return out;
    }) : [];

    // Follow-up review #4443: verificadoPor se estaba perdiendo aqui — la vista lo
    // mandaba y este body no lo incluia, asi que la atribucion llegaba n/a al log.
    const reqBody = { aprobar: payload.aprobar, items };
    if (payload.verificadoPor) reqBody.verificadoPor = String(payload.verificadoPor);
    const res = await ApiBase.post(`${BASE}/${businessId}/compras/${facturaId}/verificacion`, reqBody);
    if (!res.ok) return res;

    // FV-T5b (bit #3382): el response NO es la factura pelada -- FV-T2 la envuelve
    // en { factura, verificacionStatus, itemsCorregidos, aliasActualizados }.
    // El fallback a `body` cubre el shape plano por si el backend lo simplifica.
    const body = res.data?.data || res.data || {};
    const factura = adaptFacturaDetalle(body.factura || body);
    return {
      ok: true,
      data: {
        ...factura,
        // El status autoritativo es el que devuelve la operacion, no el del header
        // (calculado antes de aplicar las correcciones de esta misma llamada).
        verificacion_status: body.verificacionStatus ?? factura.verificacion_status ?? null,
        items_corregidos: Number(body.itemsCorregidos) || 0,
        // Cuantos alias del proveedor aprendieron el factor: la proxima factura del
        // mismo proveedor ya llega con el empaque correcto sin que nadie lo corrija.
        alias_actualizados: Number(body.aliasActualizados) || 0
      }
    };
  },

  /**
   * MV-T5 (bit #3391): estadisticas de movimientos por insumo -- cuanto se
   * desperdicia normalmente, cuanto rinde un insumo al procesarse, y que
   * movimientos se salieron de lo normal.
   *
   * Contrato CONGELADO por Argy-A en bitacora #3393:
   *   GET /businesses/{bid}/movimientos-inventario/estadisticas
   *       ?desde&hasta&semanas&insumoId&factorAnomalia
   *
   * Matices del contrato que la UI DEBE respetar:
   * - `desperdicioPromedioSemanal` es la media sobre las semanas CON actividad, no
   *   sobre la ventana completa: por eso viene `desperdicioSemanasConDatos`, para
   *   poder decir "300 g en 3 de 8 semanas" en vez de un promedio sin contexto.
   * - `aumentoRatioPromedio` es aumento/entrada (0.35 = gana 35% al procesarse).
   *   El rendimiento legible es 1 + ratio; se convierte aqui una sola vez.
   * - null != 0: null es "no hay datos", 0 es "no rinde nada".
   *
   * Devuelve `disponible:false` (sin error) cuando el endpoint aun no existe en el
   * backend, para que la vista se oculte en vez de mostrar un error que el usuario
   * no puede resolver.
   */
  async getEstadisticasMovimientos(businessId, filtros = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    const qs = new URLSearchParams();
    if (filtros.desde)          qs.set('desde', filtros.desde);
    if (filtros.hasta)          qs.set('hasta', filtros.hasta);
    if (filtros.semanas)        qs.set('semanas', String(filtros.semanas));
    if (filtros.insumoId)       qs.set('insumoId', filtros.insumoId);
    if (filtros.factorAnomalia) qs.set('factorAnomalia', String(filtros.factorAnomalia));

    const query = qs.toString();
    const res = await ApiBase.get(`${BASE}/${businessId}/movimientos-inventario/estadisticas${query ? `?${query}` : ''}`);
    if (!res.ok) {
      // 404 = backend sin MV-T4 desplegado todavia: no es un fallo que reportar.
      const status = res.status ?? res.error?.status;
      if (status === 404) return { ok: true, disponible: false, data: null };
      return res;
    }

    const d = res.data?.data || res.data || {};
    const factorAnomalia = Number(d.factorAnomalia) || 1.5;
    const insumos = (Array.isArray(d.insumos) ? d.insumos : []).map(i => {
      const promedio = i.desperdicioPromedioSemanal != null ? Number(i.desperdicioPromedioSemanal) : null;
      const ratio = i.aumentoRatioPromedio != null ? Number(i.aumentoRatioPromedio) : null;
      return {
        insumo_id: i.insumoId,
        insumo_nombre: i.insumoNombre || '',
        desperdicio_promedio_semanal: promedio,
        desperdicio_semanas_con_datos: Number(i.desperdicioSemanasConDatos) || 0,
        desperdicio_total: i.desperdicioTotal != null ? Number(i.desperdicioTotal) : null,
        aumento_ratio: ratio,
        // 0.35 -> 1.35x: lo que realmente queda tras procesar.
        aumento_rendimiento: ratio != null ? 1 + ratio : null,
        aumento_muestras: Number(i.aumentoMuestras) || 0,
        // Si muchos AUMENTOs no tienen ENTRADA previa, el ratio se calculo con
        // pocos pares y la vista debe avisar que el dato es flojo.
        aumento_sin_entrada_previa: Number(i.aumentoSinEntradaPrevia) || 0,
        totales_por_tipo: i.totalesPorTipo || {},
        // Umbral que usa el kiosk para el aviso suave (no viene del backend: se
        // deriva del promedio y el factor vigente, ambos en esta misma respuesta).
        umbral_anomalia: promedio != null ? promedio * factorAnomalia : null
      };
    });

    return {
      ok: true,
      disponible: true,
      data: {
        periodo: d.periodo || null,
        factor_anomalia: factorAnomalia,
        insumos,
        anomalias: (Array.isArray(d.anomalias) ? d.anomalias : []).map(a => ({
          movimiento_id: a.movimientoId,
          insumo_id: a.insumoId,
          insumo_nombre: a.insumoNombre || '',
          tipo: String(a.tipo || '').toLowerCase(),
          cantidad: Number(a.cantidad) || 0,
          fecha: a.fecha || '',
          promedio_comparado: Number(a.promedioComparado) || 0,
          veces: Number(a.veces) || 0
        })),
        generado_at: d.metadata?.generatedAt || null
      }
    };
  },

  /**
   * Lista movimientos manuales (desperdicios / danos / devoluciones / aumentos / salidas).
   * MV-T2 (bit #3349): codes canonicos 'desperdicio' y 'aumento'; 'merma' y 'ajuste'
   * solo sobreviven como filtro legacy para el historico ya registrado en BD.
   * @param {string} businessId
   * @param {{ tipo?:'desperdicio'|'aumento'|'dano'|'devolucion'|'salida'|'merma'|'ajuste'|'', from?:string, to?:string, insumoId?:string, page?:number, pageSize?:number }} filters
   */
  async listMovimientos(businessId, filters = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    const qs = new URLSearchParams();
    if (filters.tipo)      qs.set('tipo',     String(filters.tipo).toUpperCase());
    if (filters.from)      qs.set('desde',    filters.from);
    if (filters.to)        qs.set('hasta',    filters.to);
    if (filters.insumoId)  qs.set('insumoId', filters.insumoId);
    if (filters.usuario)   qs.set('usuario',  filters.usuario);
    if (filters.page)      qs.set('page',     String(filters.page));
    if (filters.pageSize)  qs.set('pageSize', String(filters.pageSize));

    const query = qs.toString();
    const res = await ApiBase.get(`${BASE}/${businessId}/movimientos-inventario${query ? `?${query}` : ''}`);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    const apiItems = Array.isArray(apiData.items) ? apiData.items : [];
    const items = apiItems.map(adaptMovimiento).filter(Boolean);

    return {
      ok: true,
      data: {
        items,
        totalCount: typeof apiData.total === 'number' ? apiData.total : items.length,
        page: apiData.page || 1,
        pageSize: apiData.pageSize || items.length,
        countByTipo: {
          // MV-T2 (bit #3349): contadores canonicos agregan code nuevo + legacy
          // (el historico en BD sigue teniendo MERMA/AJUSTE; no se re-migra).
          desperdicio: items.filter(m => m.tipo === 'desperdicio' || m.tipo === 'merma').length,
          aumento:     items.filter(m => m.tipo === 'aumento' || m.tipo === 'ajuste').length,
          dano:        items.filter(m => m.tipo === 'dano').length,
          devolucion:  items.filter(m => m.tipo === 'devolucion').length,
          salida:      items.filter(m => m.tipo === 'salida').length,
          // legacy puros (por si algun consumidor viejo los lee): NO sumar con los canonicos.
          merma:       items.filter(m => m.tipo === 'merma').length,
          ajuste:      items.filter(m => m.tipo === 'ajuste').length
        }
      }
    };
  },

  /**
   * Crea movimiento manual (desperdicio / dano / devolucion / aumento / salida).
   * Backend exige insumoId + cantidad + tipo. Form actual del Tab Movimiento manual
   * es heredado del mock y NO pide insumo: T4P refactorea el form para granularidad
   * por insumo. Mientras tanto, este metodo manda lo que el form provee y el backend
   * devuelve 400 si faltan campos requeridos.
   *
   * @param {string} businessId
   * @param {{ tipo:string, insumoId?:string, cantidad?:number, referencia?:string, fecha?:string, hora?:string, motivo?:string, usuario?:string, colaboradorUuid?:string, insumoSyncId?:string, insumoNombre?:string }} payload
   */
  async createMovimiento(businessId, payload) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };
    if (!payload?.tipo) return { ok: false, error: { code: 'PAYLOAD_INVALID', message: 'tipo es obligatorio' } };

    const body = {
      insumoId: payload.insumoId || payload.insumo_id || null,
      tipo: String(payload.tipo).toUpperCase(),
      cantidad: typeof payload.cantidad === 'number' ? payload.cantidad : Number(payload.cantidad) || null,
      fechaOperativa: payload.fecha || payload.fechaOperativa || null,
      horaOperativa: payload.hora || payload.horaOperativa || null,
      usuario: payload.usuario || null,
      referencia: payload.referencia || payload.motivo || null,
      // MK-T2/OPS-MOV-P1 (bit #3257/#3293, contrato confirmado por Argy-D en
      // bitacora #3261): campos NUEVOS, aditivos -- si el backend actual aun no
      // los tiene, ASP.NET ignora propiedades desconocidas sin romper nada, no
      // hace falta esperar el deploy de Argy-A para empezar a mandarlos.
      //   colaboradorUuid: atribucion canonica (usuarios.colaborador_uuid v6.8)
      //   insumoSyncId: sync_uuid REAL del insumo (Desktop matchea normalizado,
      //     con/sin guiones, case-insensitive) -- reemplaza el envio previo del
      //     PK Postgres, que generaba movimientos "fantasma" sin insumo resuelto
      //   insumoNombre: defensa doble -- fallback match por nombre / auto-create
      //     lazy del lado Desktop si el uuid no resuelve
      ...(payload.colaboradorUuid ? { colaboradorUuid: payload.colaboradorUuid } : {}),
      ...(payload.insumoSyncId ? { insumoSyncId: payload.insumoSyncId } : {}),
      ...(payload.insumoNombre ? { insumoNombre: payload.insumoNombre } : {})
    };

    const res = await ApiBase.post(`${BASE}/${businessId}/movimientos-inventario`, body);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    return { ok: true, data: adaptMovimiento(apiData) };
  },

  /**
   * Edita header de factura. Sparse: solo se envian campos definidos.
   * @param {string} businessId
   * @param {string} facturaId UUID
   * @param {{ numero?:string, fecha?:string, hora?:string, valor_base?:number, iva?:number, total?:number }} patch
   */
  async updateFactura(businessId, facturaId, patch = {}) {
    if (!businessId || !facturaId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const body = buildSparseBody(patch, {
      numero:      'codigo',
      fecha:       'fechaFactura',
      hora:        'horaFactura',
      valor_base:  'valorBase',
      iva:         'iva',
      total:       'total'
    });

    if (Object.keys(body).length === 0) {
      return { ok: false, error: { code: 'NO_CHANGES', message: 'No hay cambios que enviar' } };
    }

    const res = await ApiBase.patch(`${BASE}/${businessId}/compras/${facturaId}`, body);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    return {
      ok: true,
      data: {
        ...adaptFactura({
          facturaId: apiData.facturaId,
          businessId: apiData.businessId,
          proveedorId: apiData.proveedorId,
          codigo: apiData.codigo,
          fechaFactura: apiData.fechaFactura,
          horaFactura: apiData.horaFactura,
          moneda: apiData.moneda,
          valorBase: apiData.valorBase,
          iva: apiData.iva,
          total: apiData.total,
          createdAt: apiData.createdAt,
          matchCounts: null
        }),
        edited_at: apiData.editedAt || null,
        edited_by: apiData.editedBy || null
      }
    };
  },

  /**
   * Elimina factura (hard delete CASCADE: borra factura + items + movimientos asociados).
   *
   * Backend devuelve 409 cuando la factura tiene items MATCH con movimientos_inventario
   * asociados que requieren confirmacion explicita (proteccion contra delete accidental).
   * En ese caso, el caller debe re-invocar con `{ force: true }` para override.
   *
   * @param {string} businessId
   * @param {string} facturaId UUID
   * @param {{ force?: boolean }} options Si force=true se manda ?force=true al backend.
   */
  async deleteFactura(businessId, facturaId, options = {}) {
    if (!businessId || !facturaId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const qs = options.force ? '?force=true' : '';
    const res = await ApiBase.del(`${BASE}/${businessId}/compras/${facturaId}${qs}`);
    if (!res.ok) return res;

    return { ok: true, data: { id: facturaId, deleted: true, forced: !!options.force } };
  },

  /**
   * Edita item de factura. Sparse: solo campos definidos.
   * Si `match_status` cambia hacia/desde MATCH, el backend ajusta el movimiento_inventario
   * asociado; el response trae `movimiento_effect` ('created' | 'deleted' | 'kept' | 'noop').
   * @param {string} businessId
   * @param {string} facturaId UUID
   * @param {string} itemId UUID
   * @param {{ insumo_id?:string|null, cantidad?:number, precio_unit?:number, subtotal?:number, match_status?:string, match_score?:number, descripcion_raw?:string }} patch
   */
  async updateItem(businessId, facturaId, itemId, patch = {}) {
    if (!businessId || !facturaId || !itemId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const body = buildSparseBody(patch, {
      insumo_id:        'insumoId',
      cantidad:         'cantidad',
      precio_unit:      'precioUnitario',
      subtotal:         'totalLinea',
      match_status:     'matchStatus',
      match_score:      'matchScore',
      descripcion_raw:  'descripcionRaw',
      // FV-T5 (bit #3355): campos de empaque + flag de recalculo con subtotal
      // anclado (FV-T2: precio_unitario = total_linea / cantidad). Aditivos: si el
      // backend aun no los conoce, ASP.NET ignora propiedades desconocidas.
      cantidad_factura: 'cantidadFactura',
      factor_empaque:   'factorEmpaque',
      recalc:           'recalc',
      // COMPRAS-BORRADOR-ANULACION-T1: true marca sin inventario, false la quita; true con el item en MATCH = 409 ITEM_CASADO.
      sin_inventario:   'sinInventario'
    });

    if (Object.keys(body).length === 0) {
      return { ok: false, error: { code: 'NO_CHANGES', message: 'No hay cambios que enviar' } };
    }

    const res = await ApiBase.patch(`${BASE}/${businessId}/compras/${facturaId}/items/${itemId}`, body);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    return {
      ok: true,
      data: {
        item: adaptFacturaItem(apiData.item),
        movimiento_effect: apiData.movimientoEffect || 'noop',
        movimiento_id: apiData.movimientoId || null
      }
    };
  },

  /**
   * Edita movimiento manual. Sparse: solo campos definidos.
   * NO permite cambiar tipo, insumoId ni businessId (atomos identidad - usar DELETE+POST).
   * @param {string} businessId
   * @param {string} movId UUID
   * @param {{ cantidad?:number, fecha?:string, hora?:string, usuario?:string, referencia?:string }} patch
   */
  async updateMovimiento(businessId, movId, patch = {}) {
    if (!businessId || !movId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const body = buildSparseBody(patch, {
      cantidad:    'cantidad',
      fecha:       'fechaOperativa',
      hora:        'horaOperativa',
      usuario:     'usuario',
      referencia:  'referencia'
    });

    if (Object.keys(body).length === 0) {
      return { ok: false, error: { code: 'NO_CHANGES', message: 'No hay cambios que enviar' } };
    }

    const res = await ApiBase.patch(`${BASE}/${businessId}/movimientos-inventario/${movId}`, body);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    return { ok: true, data: adaptMovimiento(apiData) };
  },

  /**
   * Hard delete de movimiento manual. Backend devuelve 204 NoContent.
   * @param {string} businessId
   * @param {string} movId UUID
   */
  async deleteMovimiento(businessId, movId) {
    if (!businessId || !movId) return { ok: false, error: { code: 'PARAMS_REQUIRED' } };

    const res = await ApiBase.del(`${BASE}/${businessId}/movimientos-inventario/${movId}`);
    if (!res.ok) return res;

    return { ok: true, data: { id: movId, deleted: true } };
  },

  /**
   * Helper de UI: color/label estandar para un match_status.
   */
  matchStatusMeta(status) {
    const meta = {
      PENDING: { color: '#d29922', label: 'Pendiente', desc: 'Sin auto-matcheo al inventario' },
      MATCH:   { color: '#3fb950', label: 'Matcheada', desc: 'Items asociados al inventario existente' },
      REVIEW:  { color: '#f85149', label: 'Revisar',   desc: 'Matcheo parcial o ambiguo, requiere humano' }
    };
    return meta[status] || { color: '#8b949e', label: status, desc: '' };
  },

  /**
   * Helper de UI: color/label del estado de verificacion por colaborador (FV-T5).
   * Devuelve null cuando el backend aun no expone verificacion_status, para que la
   * vista sepa que NO debe pintar el badge (en vez de inventar un "Pendiente" que
   * asustaria al usuario en facturas historicas ya confirmadas).
   */
  verificacionMeta(status) {
    if (status === null || status === undefined || status === '') return null;
    const meta = {
      APPROVED: { color: '#3fb950', label: 'Verificada', icon: '✓', desc: 'Un colaborador reviso cantidades y empaques' },
      PENDING:  { color: '#d29922', label: 'En verificacion', icon: '⏳', desc: 'Falta que un colaborador confirme cantidades y factor de empaque' }
    };
    return meta[String(status).toUpperCase()] || { color: '#8b949e', label: String(status), icon: '•', desc: '' };
  },

  /**
   * Helper de UI: icono y label para tipo de movimiento.
   */
  tipoMovMeta(tipo) {
    // MV-T2 (bit #3349, canon Josse 21-jul): DESPERDICIO y AUMENTO son los codes
    // canonicos; 'merma' y 'ajuste' quedan solo como alias de LECTURA del historico
    // y se muestran SIEMPRE como Desperdicio/Aumento (la palabra "Merma" no debe
    // aparecer en ningun lado).
    const meta = {
      compra:      { icon: '\u{1F6D2}', label: 'Compra',       color: '#58a6ff' },
      desperdicio: { icon: '\u{1F4C9}', label: 'Desperdicio',  color: '#d29922' },
      merma:       { icon: '\u{1F4C9}', label: 'Desperdicio',  color: '#d29922' }, // legacy historico
      dano:        { icon: '⚠',    label: 'Daño',         color: '#f85149' },
      devolucion:  { icon: '↩',    label: 'Devolucion',   color: '#a371f7' },
      entrada:     { icon: '⬆',    label: 'Entrada',      color: '#3fb950' },
      aumento:     { icon: '➕',    label: 'Aumento',      color: '#3fb950' },
      ajuste:      { icon: '➕',    label: 'Aumento',      color: '#3fb950' }  // legacy historico
    };
    return meta[String(tipo || '').toLowerCase()] || { icon: '●', label: tipo, color: '#8b949e' };
  }
};
